import { DefaultValue } from 'recoil';
import {
  EvictableDataStore,
  PermanentDataStore,
  EvictableDataStoreKey,
  PermanentDataStoreKey,
} from 'libs/storage';

/**
 * @see https://recoiljs.org/docs/guides/atom-effects/
 */

type StorageEffect<T> = {
  setSelf: (
    param:
      | T
      | DefaultValue
      | Promise<T | DefaultValue>
      | ((param: T | DefaultValue) => T | DefaultValue),
  ) => void;
  onSet: (
    param: (newValue: T, oldValue: T | DefaultValue, isReset: boolean) => void,
  ) => void;
};

/**
 * @linear:ENG-2541 This will be removed in favor of permanentStorageEffect and
 * evictableStorageEffect as per RFC-0011.
 */
export const localStorageEffect =
  <T>(key: string, merge?: (value: T) => T) =>
  ({ setSelf, onSet }: StorageEffect<T>) => {
    const savedValue = localStorage.getItem(key);

    if (savedValue != null) {
      let value = JSON.parse(savedValue);
      if (merge) value = merge(value);
      setSelf(value);
    }

    onSet((newValue: T, _, isReset: boolean) => {
      isReset
        ? localStorage.removeItem(key)
        : localStorage.setItem(key, JSON.stringify(newValue));
    });
  };

export const permanentStorageEffect =
  <T>(key: PermanentDataStoreKey, merge?: (value: T) => T) =>
  ({ setSelf, onSet }: StorageEffect<T>) => {
    // Initialize with stored value
    const loadStoredValue = async (): Promise<void> => {
      const savedValue = await PermanentDataStore.getItem<T>(key);
      if (savedValue !== undefined) {
        setSelf(merge ? merge(savedValue) : savedValue);
      }
    };

    // Load initial value
    loadStoredValue().then(() => {
      // Subscribe to changes
      onSet(async (newValue, _, isReset) => {
        if (isReset) {
          await PermanentDataStore.removeItem(key);
        } else {
          await PermanentDataStore.setItem(key, newValue);
        }
      });
    });
  };

export const evictableStorageEffect =
  <T>(key: EvictableDataStoreKey, merge?: (value: T) => T) =>
  ({ setSelf, onSet }: StorageEffect<T>) => {
    // Initialize with stored value
    const loadStoredValue = async (): Promise<void> => {
      const savedValue = await EvictableDataStore.getItem<T>(key);
      if (savedValue !== undefined) {
        setSelf(merge ? merge(savedValue) : savedValue);
      }
    };

    // Load initial value
    loadStoredValue().then(() => {
      // Subscribe to changes
      onSet(async (newValue, _, isReset) => {
        if (isReset) {
          await EvictableDataStore.removeItem(key);
        } else {
          await EvictableDataStore.setItem(key, newValue);
        }
      });
    });
  };
